import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  ArrowRight,
  BarChart3,
  Boxes,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  ExternalLink,
  FileText,
  Gauge,
  Image as ImageIcon,
  Layers3,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  PackageCheck,
  Plus,
  RefreshCw,
  Route as RouteIcon,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Truck,
  UserRound,
  Users,
  X,
  Zap,
} from "lucide-react";
import { api } from "./api";
import AuditWorkspace from "./AuditWorkspace";
import type {
  AuditRecord,
  AppPayload,
  FleetProcess,
  ProductiveSettings,
  Role,
  Stage,
} from "./types";

type Page = "queue" | "schedule" | "history" | "audit" | "productive" | "integrations" | "admin";

const PRODUCTIVE_STARTED_AT = Date.parse("2026-09-01T00:00:00.000Z");
const PRODUCTIVE_ROLES: Role[] = ["FROTAS", "COMERCIAL", "DIRETORIA"];
const TABLE_PAGE_SIZE = 60;
const SHEET_BRANDS = ["Copa", "Ultra", "Liqui", "Super"] as const;
const STATUS_U_OPTIONS = [
  "Preparação ADM",
  "Produção",
  "Conferência",
  "Mat. Expedição",
  "Mat. Adesivador",
  "Instalação",
  "Finalizado",
  "Objeção",
  "Cancelado",
  "Pendencia BPO",
  "Transportadora",
  "Cancelado BP One",
  "Pausado",
] as const;
const STATUS_COMMERCIAL_OPTIONS = ["Liberar OS", "OS Liberada", "Conferir OS", "Faturar OS", "OS Faturada"] as const;
const TERMINAL_STAGES: Stage[] = ["CONCLUIDO", "CANCELADO", "CANCELADO_BP_ONE"];

type QueueBucket = "urgent" | "now" | "scheduled";

interface ProcessGroup {
  key: string;
  label: string;
  company: string;
  items: FleetProcess[];
  bucket: QueueBucket;
  forecast: string | null;
}

const ROLE_LABELS: Record<Role, string> = {
  ADMIN: "Acesso principal",
  FROTAS: "Frotas",
  COMERCIAL: "Comercial",
  ARTE: "Arte Final",
  PRODUCAO: "Produção",
  FINANCEIRO: "Financeiro",
  DIRETORIA: "Diretoria",
};

const STAGE_LABELS: Record<Stage, string> = {
  PREPARACAO_ADM: "Preparação adm",
  ARTE_OS_PARALELO: "Comercial + Arte",
  ARTE_FINALIZADA: "Arte finalizada",
  PROGRAMADO_PRODUCAO: "Programado",
  PRODUCAO: "Produção",
  SEPARACAO: "Separação",
  CONFERENCIA: "Conferência",
  EXPEDICAO: "Expedição",
  ROTA: "Rota",
  INSTALACAO: "Instalação",
  POS_FINALIZACAO: "Auditoria + pagamento",
  PRONTO_FINANCEIRO: "Pronto para faturar",
  FATURAMENTO: "Faturamento",
  CONCLUIDO: "Concluído",
  FINALIZADO_SEM_AJ: "Finalizado · Status Comercial pendente",
  PAUSADO: "Pausado",
  OBJECAO: "Objeção",
  PENDENCIA_BPO: "Pendencia BPO",
  CANCELADO: "Cancelado",
  CANCELADO_BP_ONE: "Cancelado BP One",
  TRANSPORTADORA: "Transportadora",
};

const STAGE_GROUPS: Array<{
  key: string;
  label: string;
  owner: string;
  stages: Stage[];
  icon: typeof Truck;
}> = [
  {
    key: "intake",
    label: "Entrada e SAC",
    owner: "Frotas",
    stages: ["PREPARACAO_ADM"],
    icon: Truck,
  },
  {
    key: "parallel",
    label: "O.S. + Arte",
    owner: "Comercial · Arte",
    stages: ["ARTE_OS_PARALELO", "ARTE_FINALIZADA"],
    icon: ImageIcon,
  },
  {
    key: "production",
    label: "Produção",
    owner: "Produção",
    stages: ["PROGRAMADO_PRODUCAO", "PRODUCAO", "SEPARACAO"],
    icon: Boxes,
  },
  {
    key: "conference",
    label: "Conferência",
    owner: "Produção · Frotas",
    stages: ["CONFERENCIA"],
    icon: ClipboardCheck,
  },
  {
    key: "expedition",
    label: "Mat. Expedição",
    owner: "Devolve a Frotas",
    stages: ["EXPEDICAO"],
    icon: PackageCheck,
  },
  {
    key: "field",
    label: "Mat. Adesivador / Instalação",
    owner: "Frotas",
    stages: ["ROTA", "INSTALACAO", "TRANSPORTADORA"],
    icon: RouteIcon,
  },
  {
    key: "controls",
    label: "Controles finais",
    owner: "Frotas",
    stages: ["POS_FINALIZACAO"],
    icon: ClipboardCheck,
  },
  {
    key: "finance",
    label: "Faturar OS",
    owner: "Financeiro",
    stages: ["PRONTO_FINANCEIRO", "FATURAMENTO"],
    icon: CircleDollarSign,
  },
  {
    key: "pending",
    label: "Ajustes e pendências",
    owner: "Frotas",
    stages: ["FINALIZADO_SEM_AJ", "PAUSADO", "OBJECAO", "PENDENCIA_BPO"],
    icon: AlertCircle,
  },
];

const DEMO_ACCOUNTS = [
  { email: "101c12025@gmail.com", label: "Acesso principal", role: "ADMIN" },
  { email: "frotas@c1sign.com.br", label: "Frotas", role: "FROTAS" },
  { email: "comercial@c1sign.com.br", label: "Comercial", role: "COMERCIAL" },
  { email: "arte@c1sign.com.br", label: "Arte Final", role: "ARTE" },
  { email: "producao@c1sign.com.br", label: "Produção", role: "PRODUCAO" },
  { email: "financeiro@c1sign.com.br", label: "Financeiro", role: "FINANCEIRO" },
  { email: "diretoria@c1sign.com.br", label: "Diretoria", role: "DIRETORIA" },
] as const;

const ROLE_STAGE_QUEUE: Record<Role, Stage[]> = {
  ADMIN: Object.keys(STAGE_LABELS) as Stage[],
  FROTAS: [
    "PREPARACAO_ADM",
    "EXPEDICAO",
    "ROTA",
    "INSTALACAO",
    "POS_FINALIZACAO",
    "FINALIZADO_SEM_AJ",
    "PAUSADO",
    "OBJECAO",
    "PENDENCIA_BPO",
    "TRANSPORTADORA",
  ],
  COMERCIAL: ["ARTE_OS_PARALELO"],
  ARTE: ["ARTE_OS_PARALELO", "ARTE_FINALIZADA"],
  PRODUCAO: ["PROGRAMADO_PRODUCAO", "PRODUCAO", "SEPARACAO", "CONFERENCIA"],
  FINANCEIRO: ["PRONTO_FINANCEIRO", "FATURAMENTO"],
  DIRETORIA: Object.keys(STAGE_LABELS) as Stage[],
};

function formatDate(value: string | null, withTime = false): string {
  if (!value) return "—";
  const normalized = value.includes("T") ? value : `${value}T12:00:00`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

function durationAverage(
  processes: FleetProcess[],
  startField: keyof FleetProcess,
  endField: keyof FleetProcess,
): { averageHours: number | null; samples: number } {
  const values = processes.flatMap((process) => {
    const start = Date.parse(String(process[startField] || ""));
    const end = Date.parse(String(process[endField] || ""));
    return Number.isFinite(start) && Number.isFinite(end) && end >= start
      ? [(end - start) / 3_600_000]
      : [];
  });
  return {
    averageHours: values.length
      ? Math.round((values.reduce((total, value) => total + value, 0) / values.length) * 10) / 10
      : null,
    samples: values.length,
  };
}

function formatDuration(hours: number | null): string {
  if (hours === null) return "Coletando";
  if (hours === 0) return "0 min";
  if (hours < 24) return `${hours.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}h`;
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours % 24);
  return `${days}d${rest ? ` ${rest}h` : ""}`;
}

function formatMoney(value: number): string {
  return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function auditTone(value: string): string {
  const status = normalizedStatus(value);
  if (status === "liberada" || status === "pago" || status === "enviada") return "is-success";
  if (status === "nao faturar") return "is-danger";
  if (status.includes("aguardando") || status === "pendente" || status === "nao pago") return "is-warning";
  return "is-neutral";
}

function normalizedStatus(value: string): string {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
}

function normalizedBrand(value: string): string {
  const brand = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  if (brand.includes("copa")) return "Copa";
  if (brand.includes("ulta") || brand.includes("ultra")) return "Ultra";
  if (brand.includes("liqui")) return "Liqui";
  if (brand.includes("super")) return "Super";
  return "Outros";
}

function shortName(name: string): string {
  return name.split(" ").filter(Boolean).slice(0, 2).join(" ");
}

function cn(value: Array<string | false | null | undefined>): string {
  return value.filter(Boolean).join(" ");
}

function processForecast(process: FleetProcess): string | null {
  if (process.stage === "ARTE_OS_PARALELO") return process.art_forecast_at;
  if (["ROTA", "INSTALACAO", "TRANSPORTADORA"].includes(process.stage)) return process.route_forecast_at;
  return process.production_forecast_at;
}

function startOfToday(): number {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function endOfToday(): number {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date.getTime();
}

function processIsLate(process: FleetProcess): boolean {
  const forecast = processForecast(process);
  return Boolean(forecast && Date.parse(forecast) < startOfToday());
}

function processQueueBucket(process: FleetProcess): QueueBucket {
  if (processIsLate(process) || ["ALTA", "PILOTO"].includes(process.priority.toUpperCase())) return "urgent";
  const forecast = processForecast(process);
  return forecast && Date.parse(forecast) > endOfToday() ? "scheduled" : "now";
}

function buildProcessGroups(processes: FleetProcess[]): ProcessGroup[] {
  const groups = new Map<string, FleetProcess[]>();

  processes.forEach((process) => {
    const key = process.os_number
      ? `os-${process.os_number}-${process.company}`
      : `bd-${process.bd}-${process.id}`;
    groups.set(key, [...(groups.get(key) || []), process]);
  });

  return [...groups.entries()].map(([key, items]) => {
    const forecasts = items.map(processForecast).filter((value): value is string => Boolean(value));
    const forecast = forecasts.length
      ? forecasts.sort((left, right) => Date.parse(left) - Date.parse(right))[0]
      : null;
    const itemBuckets = items.map(processQueueBucket);
    const bucket: QueueBucket = itemBuckets.includes("urgent")
      ? "urgent"
      : itemBuckets.includes("now")
        ? "now"
        : "scheduled";

    return {
      key,
      label: items[0].os_number ? `O.S. ${items[0].os_number}` : `BD ${items[0].bd}`,
      company: items[0].company,
      items,
      bucket,
      forecast,
    };
  }).sort((left, right) => {
    const order: Record<QueueBucket, number> = { urgent: 0, now: 1, scheduled: 2 };
    if (order[left.bucket] !== order[right.bucket]) return order[left.bucket] - order[right.bucket];
    return Date.parse(left.forecast || "2999-12-31") - Date.parse(right.forecast || "2999-12-31");
  });
}

function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <div className={cn(["brand-lockup", compact && "is-compact"])} aria-label="C1 Sign">
      <span className="brand-mark" aria-hidden="true">
        <img src="/c1-logo.png" alt="" />
      </span>
      {!compact && (
        <span className="brand-copy">
          <strong>FLUXO FROTAS</strong>
          <small>Operação C1 Sign</small>
        </span>
      )}
    </div>
  );
}

function Login({ onLogin }: { onLogin: (payload: AppPayload) => void }) {
  const [email, setEmail] = useState("101c12025@gmail.com");
  const [password, setPassword] = useState("FluxoC1#2026");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      onLogin(await api.login(email, password));
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Falha ao entrar.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-story" aria-label="Apresentação do Fluxo Frotas">
        <Logo />
        <div className="login-hero-copy">
          <p className="eyebrow light">OPERAÇÃO CONECTADA</p>
          <h1>
            Cada veículo no lugar certo,
            <span> no tempo certo.</span>
          </h1>
          <p>
            Uma linha de produção visível de ponta a ponta: do SAC ao faturamento,
            com previsão, responsável e próximo passo.
          </p>
        </div>
        <div className="login-rail" aria-hidden="true">
          {STAGE_GROUPS.slice(0, 6).map((group, index) => (
            <span key={group.key} style={{ "--delay": `${index * 90}ms` } as React.CSSProperties}>
              {index + 1}
            </span>
          ))}
        </div>
        <p className="login-footnote">Ambiente de homologação · dados demonstrativos</p>
      </section>

      <section className="login-panel">
        <div className="login-mobile-logo"><Logo /></div>
        <form className="login-card" onSubmit={submit}>
          <div>
            <p className="eyebrow">ACESSO POR SETOR</p>
            <h2>Bem-vindo ao fluxo.</h2>
            <p className="muted">Escolha um perfil de teste ou informe seu acesso.</p>
          </div>

          <label>
            E-mail
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            Senha
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>

          {message && (
            <p className="form-error" role="alert">
              <AlertCircle size={16} /> {message}
            </p>
          )}

          <button className="primary-button wide" disabled={busy}>
            {busy ? <Loader2 className="spin" size={18} /> : <ArrowRight size={18} />}
            {busy ? "Entrando..." : "Entrar no sistema"}
          </button>

          <div className="demo-access">
            <div className="demo-access-title">
              <span>Perfis para teste</span>
              <code>FluxoC1#2026</code>
            </div>
            <div className="demo-account-grid">
              {DEMO_ACCOUNTS.map((account) => (
                <button
                  key={account.email}
                  type="button"
                  className={email === account.email ? "is-active" : ""}
                  onClick={() => {
                    setEmail(account.email);
                    setPassword("FluxoC1#2026");
                  }}
                >
                  <span>{account.label}</span>
                  <small>{account.role}</small>
                </button>
              ))}
            </div>
          </div>
        </form>
      </section>
    </main>
  );
}

function QueueProcessRow({ process, onSelect }: { process: FleetProcess; onSelect: (process: FleetProcess) => void }) {
  const forecast = processForecast(process);
  return (
    <div className="queue-process-row">
      <div className="queue-vehicle-identity">
        <Truck size={17} />
        <div>
          <strong>{[process.vehicle_type, process.vehicle_model].filter(Boolean).join(" · ") || "Veículo não informado"}</strong>
          <small>{process.vehicle_plate ? `Placa ${process.vehicle_plate}` : `BD ${process.bd} · placa não informada`}</small>
        </div>
      </div>
      <div className="queue-process-state">
        <span className={`stage-pill stage-${process.stage.toLowerCase()}`}>{STAGE_LABELS[process.stage]}</span>
        <small className={processIsLate(process) ? "is-late" : ""}>
          {forecast ? formatDate(forecast, true) : "Sem previsão"}
        </small>
      </div>
      <div className="queue-process-action">
        <span>{process.next_action}</span>
        <button type="button" onClick={() => onSelect(process)}>Abrir e agir <ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

function QueueGroupCard({ group, onSelect }: { group: ProcessGroup; onSelect: (process: FleetProcess) => void }) {
  if (group.items.length === 1) {
    return (
      <article className="queue-os-card is-single">
        <header className="queue-os-header">
          <div><strong>{group.label}</strong><span>{group.company}</span></div>
          <span className="vehicle-count">1 veículo</span>
        </header>
        <QueueProcessRow process={group.items[0]} onSelect={onSelect} />
      </article>
    );
  }

  return (
    <details className="queue-os-card">
      <summary className="queue-os-header">
        <div><strong>{group.label}</strong><span>{group.company}</span></div>
        <span className="group-summary-meta">
          {group.forecast && <small>{formatDate(group.forecast, true)}</small>}
          <span className="vehicle-count">{group.items.length} veículos</span>
          <ChevronRight className="group-chevron" size={18} />
        </span>
      </summary>
      <div className="queue-group-vehicles">
        {group.items.map((process) => <QueueProcessRow key={process.id} process={process} onSelect={onSelect} />)}
      </div>
    </details>
  );
}

function ActionQueue({ processes, onSelect }: { processes: FleetProcess[]; onSelect: (process: FleetProcess) => void }) {
  const groups = buildProcessGroups(processes);
  const sections: Array<{ key: QueueBucket; title: string; description: string; icon: typeof AlertCircle }> = [
    { key: "urgent", title: "Atrasados ou prioritários", description: "Comece por estes processos.", icon: AlertCircle },
    { key: "now", title: "Para fazer agora", description: "Ações disponíveis para o seu setor.", icon: Zap },
    { key: "scheduled", title: "Programados", description: "Já estão na sua fila, mas têm prazo futuro.", icon: CalendarClock },
  ];

  if (!groups.length) {
    return (
      <div className="empty-state queue-empty">
        <CheckCircle2 size={30} />
        <strong>Sua fila está em dia.</strong>
        <span>Nenhuma ação depende do seu setor neste recorte.</span>
      </div>
    );
  }

  return (
    <div className="action-queue">
      {sections.map((section) => {
        const sectionGroups = groups.filter((group) => group.bucket === section.key);
        if (!sectionGroups.length) return null;
        const Icon = section.icon;
        const vehicleCount = sectionGroups.reduce((total, group) => total + group.items.length, 0);
        return (
          <section key={section.key} className={`queue-section queue-${section.key}`}>
            <header>
              <span className="queue-section-icon"><Icon size={17} /></span>
              <div><h3>{section.title}</h3><p>{section.description}</p></div>
              <strong>{sectionGroups.length} O.S. · {vehicleCount} {vehicleCount === 1 ? "veículo" : "veículos"}</strong>
            </header>
            <div className="queue-section-list">
              {sectionGroups.map((group) => <QueueGroupCard key={group.key} group={group} onSelect={onSelect} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ProcessPath({ process }: { process: FleetProcess }) {
  const currentIndex = Math.max(0, STAGE_GROUPS.findIndex((group) => group.stages.includes(process.stage)));
  const current = STAGE_GROUPS[currentIndex];
  const next = STAGE_GROUPS[currentIndex + 1];

  return (
    <section className="process-path" aria-label="Trilho produtivo do processo">
      <div className="process-path-heading">
        <div><span>TRILHO PRODUTIVO</span><strong>Etapa {currentIndex + 1} de {STAGE_GROUPS.length}</strong></div>
        <b>{current.label}</b>
      </div>
      <div className="process-path-track" aria-hidden="true">
        {STAGE_GROUPS.map((group, index) => (
          <i key={group.key} className={cn([index < currentIndex && "is-complete", index === currentIndex && "is-current"])} />
        ))}
      </div>
      <p>{next ? <>Depois: <strong>{next.label}</strong> · {next.owner}</> : "Última etapa do fluxo."}</p>
    </section>
  );
}

function ProcessTable({
  processes,
  selectedId,
  onSelect,
}: {
  processes: FleetProcess[];
  selectedId: number | null;
  onSelect: (process: FleetProcess) => void;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>O.S. / BD</th>
            <th>Cliente</th>
            <th>Veículo</th>
            <th>Marca</th>
            <th>Status (U)</th>
            <th>Previsão</th>
            <th>Próxima ação</th>
            <th aria-label="Abrir" />
          </tr>
        </thead>
        <tbody>
          {processes.map((process) => {
            const forecast =
              process.stage === "ARTE_OS_PARALELO"
                ? process.art_forecast_at
                : process.stage === "ROTA" || process.stage === "INSTALACAO"
                  ? process.route_forecast_at
                  : process.production_forecast_at;
            return (
              <tr
                key={process.id}
                className={selectedId === process.id ? "is-selected" : ""}
                onClick={() => onSelect(process)}
              >
                <td>
                  <strong>{process.os_number ? `O.S. ${process.os_number}` : "O.S. pendente"}</strong>
                  <small>BD {process.bd}</small>
                </td>
                <td>
                  <strong>{process.company}</strong>
                  <small>{process.city}{process.state ? ` · ${process.state}` : ""}</small>
                </td>
                <td>
                  <strong>{[process.vehicle_type, process.vehicle_model].filter(Boolean).join(" · ") || "Não informado"}</strong>
                  <small>{process.vehicle_plate ? `Placa ${process.vehicle_plate}` : "Placa não informada"}</small>
                </td>
                <td><span className={`brand-pill brand-${process.brand.toLowerCase()}`}>{process.brand}</span></td>
                <td>
                  <span className={`stage-pill stage-${process.stage.toLowerCase()}`}>{process.fleet_status}</span>
                  {process.fleet_status === "Finalizado" && <small>Status Comercial: {process.os_status}</small>}
                </td>
                <td>
                  <span className={cn(["forecast", forecast && Date.parse(forecast) < Date.now() && process.stage !== "CONCLUIDO" && "is-late"])}>
                    <Clock3 size={14} /> {formatDate(forecast, true)}
                  </span>
                </td>
                <td><span className="next-action">{process.next_action}</span></td>
                <td><button className="icon-button" aria-label={`Abrir ${process.os_number ? `O.S. ${process.os_number}` : "O.S. pendente"}, BD ${process.bd}`}><ChevronRight size={17} /></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!processes.length && (
        <div className="empty-state">
          <CheckCircle2 size={28} />
          <strong>Nenhum processo neste recorte.</strong>
          <span>Ajuste os filtros ou consulte outra etapa.</span>
        </div>
      )}
    </div>
  );
}

interface ActionDefinition {
  key: string;
  label: string;
  hint: string;
  tone?: "primary" | "success";
  payload?: Record<string, unknown>;
}

function ProcessDrawer({
  process,
  userRole,
  events,
  onClose,
  onMutate,
}: {
  process: FleetProcess;
  userRole: Role;
  events: AppPayload["events"];
  onClose: () => void;
  onMutate: (action: string, payload: Record<string, unknown>) => Promise<void>;
}) {
  const [busy, setBusy] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [draft, setDraft] = useState({
    osNumber: process.os_number || "",
    artForecast: process.art_forecast_at?.slice(0, 16) || "",
    productionForecast: process.production_forecast_at?.slice(0, 16) || "",
    artFile: process.art_file_url || "",
    routeTeam: process.route_team || "",
    routeForecast: process.route_forecast_at?.slice(0, 16) || "",
    invoiceNumber: process.invoice_number || "",
    jointRequired: Boolean(process.joint_check_required),
  });

  useEffect(() => {
    setDraft({
      osNumber: process.os_number || "",
      artForecast: process.art_forecast_at?.slice(0, 16) || "",
      productionForecast: process.production_forecast_at?.slice(0, 16) || "",
      artFile: process.art_file_url || "",
      routeTeam: process.route_team || "",
      routeForecast: process.route_forecast_at?.slice(0, 16) || "",
      invoiceNumber: process.invoice_number || "",
      jointRequired: Boolean(process.joint_check_required),
    });
  }, [process]);

  const can = (...roles: Role[]) => userRole === "ADMIN" || roles.includes(userRole);
  const actions: ActionDefinition[] = [];
  if (process.stage === "PREPARACAO_ADM" && can("FROTAS")) {
    actions.push({ key: "release_operations", label: "Concluir SAC e liberar", hint: "Libera Comercial e Arte ao mesmo tempo", tone: "primary", payload: { sequence: process.sequence } });
  }
  if (process.stage === "ARTE_OS_PARALELO") {
    if (process.os_status !== "O.S. liberada" && can("COMERCIAL", "FROTAS")) {
      actions.push({ key: "release_os", label: "Lançar e liberar O.S.", hint: "A tarefa sai da fila Comercial", payload: { os_number: draft.osNumber } });
    }
    if (can("ARTE")) {
      actions.push({ key: "schedule_art", label: "Salvar previsão da Arte", hint: "Visível para Produção e Diretoria", payload: { forecast: draft.artForecast } });
      actions.push({ key: "finish_art", label: "Finalizar arte e liberar", hint: "Exige o link dos arquivos", tone: "primary", payload: { file_url: draft.artFile } });
    }
  }
  if (process.stage === "PROGRAMADO_PRODUCAO" && can("PRODUCAO")) {
    actions.push({ key: "schedule_production", label: "Salvar previsão da produção", hint: "Atualiza a programação de Frotas", payload: { forecast: draft.productionForecast } });
    actions.push({ key: "start_production", label: "Iniciar produção", hint: "Registra início real", tone: "primary" });
  }
  if (process.stage === "PRODUCAO" && can("PRODUCAO")) {
    actions.push({ key: "finish_production", label: "Concluir produção", hint: "Inicia a etapa de separação", tone: "primary" });
  }
  if (process.stage === "SEPARACAO" && can("PRODUCAO")) {
    actions.push({ key: "finish_separation", label: "Concluir separação", hint: "Prepara o material para conferência", tone: "primary" });
  }
  if (process.stage === "CONFERENCIA") {
    if (!process.conference_started_at && can("PRODUCAO")) {
      actions.push({ key: "start_conference", label: "Iniciar conferência", hint: draft.jointRequired ? "Solicitará presença de Frotas" : "Conferência interna da Produção", payload: { joint_required: draft.jointRequired } });
    }
    if (process.joint_check_required && !process.joint_check_completed_at && can("PRODUCAO", "FROTAS")) {
      actions.push({ key: "finish_joint_check", label: "Concluir conferência conjunta", hint: "Confirma participação de Frotas", tone: "primary" });
    }
    if ((!process.joint_check_required || process.joint_check_completed_at) && can("PRODUCAO")) {
      actions.push({ key: "finish_conference", label: "Finalizar para expedição", hint: "Devolve o processo ao setor de Frotas", tone: "success" });
    }
  }
  if (process.stage === "EXPEDICAO" && can("FROTAS")) {
    actions.push({ key: "receive_expedition", label: "Receber e definir rota", hint: "Vincula equipe e previsão de instalação", tone: "primary", payload: { route_team: draft.routeTeam, forecast: draft.routeForecast } });
  }
  if (process.stage === "ROTA" && can("FROTAS")) {
    actions.push({ key: "start_installation", label: "Liberar para instalação", hint: "Registra início em campo", tone: "primary" });
  }
  if (process.stage === "INSTALACAO" && can("FROTAS")) {
    actions.push({ key: "finish_installation", label: "Finalizar trabalho", hint: "Abre Auditoria e Pagamento em paralelo", tone: "success" });
  }
  if (process.stage === "FINALIZADO_SEM_AJ" && can("FROTAS")) {
    actions.push({ key: "open_controls", label: "Definir Status Comercial como Conferir OS", hint: "Abre Auditoria e Pagamento em paralelo", tone: "primary" });
  }
  if (process.stage === "PRONTO_FINANCEIRO" && can("FINANCEIRO")) {
    actions.push({ key: "invoice", label: "Registrar faturamento", hint: "Informe o número da nota fiscal", tone: "primary", payload: { invoice_number: draft.invoiceNumber } });
  }
  if (process.stage === "FATURAMENTO" && can("FINANCEIRO")) {
    actions.push({ key: "complete_finance", label: "Concluir faturamento", hint: "Encerra o processo", tone: "success" });
  }

  async function runAction(definition: ActionDefinition) {
    setBusy(definition.key);
    setErrorMessage("");
    try {
      await onMutate(definition.key, definition.payload || {});
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "Falha ao atualizar.");
    } finally {
      setBusy("");
    }
  }

  const processEvents = events.filter((event) => event.process_id === process.id).slice(0, 8);

  return (
    <div className="drawer-layer" role="presentation">
      <button className="drawer-backdrop" onClick={onClose} aria-label="Fechar detalhes" />
      <aside className="process-drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
        <header className="drawer-header">
          <div>
            <p className="eyebrow">PROCESSO {process.code}</p>
            <h2 id="drawer-title">{process.os_number ? `O.S. ${process.os_number}` : "O.S. pendente"} <span>· BD {process.bd}</span></h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
        </header>

        <div className="drawer-scroll">
          <section className="process-identity">
            <div className="identity-topline">
              <span className={`brand-pill brand-${process.brand.toLowerCase()}`}>{process.brand}</span>
              <span className={`stage-pill stage-${process.stage.toLowerCase()}`}>{STAGE_LABELS[process.stage]}</span>
              <span className={`priority-pill priority-${process.priority.toLowerCase()}`}>{process.priority}</span>
            </div>
            <h3>{process.company}</h3>
            <p>{process.city}{process.state ? ` · ${process.state}` : ""} {process.category ? `· ${process.category}` : ""}</p>
            <div className="next-action-callout">
              <Zap size={18} />
              <div><small>PRÓXIMA AÇÃO</small><strong>{process.next_action}</strong></div>
            </div>
          </section>

          <ProcessPath process={process} />

          <section className="drawer-section">
            <div className="drawer-section-title"><CalendarClock size={17} /><h3>Previsões e responsáveis</h3></div>
            <div className="detail-grid">
              <div><span>Tipo de veículo</span><strong>{process.vehicle_type || "—"}</strong></div>
              <div><span>Modelo</span><strong>{process.vehicle_model || "—"}</strong></div>
              <div><span>Placa</span><strong>{process.vehicle_plate || "—"}</strong></div>
              <div><span>Arte Final</span><strong>{formatDate(process.art_forecast_at, true)}</strong></div>
              <div><span>Produção</span><strong>{formatDate(process.production_forecast_at, true)}</strong></div>
              <div><span>Rota / instalação</span><strong>{formatDate(process.route_forecast_at, true)}</strong></div>
              <div><span>Equipe</span><strong>{process.route_team || process.team || "—"}</strong></div>
            </div>
          </section>

          {actions.length > 0 && (
            <section className="drawer-section action-zone">
              <div className="drawer-section-title"><Sparkles size={17} /><h3>Ações do seu setor</h3></div>
              {(process.stage === "ARTE_OS_PARALELO" && can("COMERCIAL", "FROTAS")) && (
                <label>O.S.<input value={draft.osNumber} onChange={(event) => setDraft({ ...draft, osNumber: event.target.value })} placeholder="Número da ordem de serviço" /></label>
              )}
              {(process.stage === "ARTE_OS_PARALELO" && can("ARTE")) && (
                <div className="action-fields two-columns">
                  <label>Previsão da arte<input type="datetime-local" value={draft.artForecast} onChange={(event) => setDraft({ ...draft, artForecast: event.target.value })} /></label>
                  <label>Link dos arquivos<input type="url" value={draft.artFile} onChange={(event) => setDraft({ ...draft, artFile: event.target.value })} placeholder="https://drive.google.com/..." /></label>
                </div>
              )}
              {(process.stage === "PROGRAMADO_PRODUCAO" && can("PRODUCAO")) && (
                <label>Previsão da produção<input type="datetime-local" value={draft.productionForecast} onChange={(event) => setDraft({ ...draft, productionForecast: event.target.value })} /></label>
              )}
              {(process.stage === "CONFERENCIA" && !process.conference_started_at && can("PRODUCAO")) && (
                <label className="toggle-field"><input type="checkbox" checked={draft.jointRequired} onChange={(event) => setDraft({ ...draft, jointRequired: event.target.checked })} /><span><strong>Conferência conjunta</strong><small>Solicitar participação do setor de Frotas</small></span></label>
              )}
              {(process.stage === "EXPEDICAO" && can("FROTAS")) && (
                <div className="action-fields two-columns">
                  <label>Equipe da rota<input value={draft.routeTeam} onChange={(event) => setDraft({ ...draft, routeTeam: event.target.value })} placeholder="Ex.: Equipe Interior" /></label>
                  <label>Previsão de instalação<input type="datetime-local" value={draft.routeForecast} onChange={(event) => setDraft({ ...draft, routeForecast: event.target.value })} /></label>
                </div>
              )}
              {(process.stage === "PRONTO_FINANCEIRO" && can("FINANCEIRO")) && (
                <label>Número da nota fiscal<input value={draft.invoiceNumber} onChange={(event) => setDraft({ ...draft, invoiceNumber: event.target.value })} placeholder="Ex.: NF-9012" /></label>
              )}
              {errorMessage && <p className="form-error" role="alert"><AlertCircle size={15} /> {errorMessage}</p>}
              <div className="action-list">
                {actions.map((action) => (
                  <button key={action.key} className={cn(["workflow-action", action.tone === "success" ? "is-success" : action.tone === "primary" && "is-primary"])} onClick={() => runAction(action)} disabled={Boolean(busy)}>
                    <span>{busy === action.key ? <Loader2 className="spin" size={18} /> : <Check size={18} />}</span>
                    <div><strong>{action.label}</strong><small>{action.hint}</small></div>
                    <ChevronRight size={17} />
                  </button>
                ))}
              </div>
            </section>
          )}

          {process.stage === "POS_FINALIZACAO" && (
            <section className="drawer-section">
              <div className="drawer-section-title"><ShieldCheck size={17} /><h3>Controles sincronizados</h3></div>
              <div className="control-cards">
                <div><ClipboardCheck size={18} /><div><strong>Auditoria O.S.</strong><small>{process.audit_status}</small></div><span className={cn(["audit-state-dot", auditTone(process.audit_status)])} /></div>
                <div><CircleDollarSign size={18} /><div><strong>Pagamento da equipe</strong><small>{process.payment_status}</small></div><span className={cn(["audit-state-dot", auditTone(process.payment_status)])} /></div>
              </div>
              <p className="synced-note">Atualizado automaticamente pelo histórico da Auditoria O.S.{process.audit_updated_at ? ` · ${formatDate(process.audit_updated_at, true)}` : ""}</p>
              {process.audit_pdf_url && <a className="inline-document-link" href={process.audit_pdf_url} target="_blank" rel="noreferrer noopener"><FileText size={15} /> Abrir relatório desta O.S. <ExternalLink size={13} /></a>}
            </section>
          )}

          <section className="drawer-section">
            <div className="drawer-section-title"><Activity size={17} /><h3>Histórico recente</h3></div>
            <div className="timeline">
              {processEvents.map((event) => (
                <div key={event.id} className="timeline-item">
                  <i />
                  <div><strong>{event.action}</strong><span>{event.user_name} · {ROLE_LABELS[event.user_role]}</span>{event.note && <p>{event.note}</p>}</div>
                  <time>{formatDate(event.created_at, true)}</time>
                </div>
              ))}
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

function ProductiveView({ processes, settings }: { processes: FleetProcess[]; settings: ProductiveSettings }) {
  const active = processes.filter((item) => !TERMINAL_STAGES.includes(item.stage));
  const inProgress = processes.filter((item) => ["Produção", "Instalação", "Mat. Adesivador", "Mat. Expedição"].includes(item.fleet_status));
  const observed = processes.filter((item) => Date.parse(item.created_at) >= PRODUCTIVE_STARTED_AT);
  const brandCounts = useMemo(() => {
    return SHEET_BRANDS.map((brand) => [
      brand,
      active.filter((item) => normalizedBrand(item.brand) === brand).length,
    ] as const);
  }, [active]);
  const maxBrand = Math.max(...brandCounts.map(([, count]) => count), 1);
  const upcoming = [...active]
    .map((item) => ({ item, date: item.route_forecast_at || item.production_forecast_at || item.art_forecast_at }))
    .filter((entry) => entry.date)
    .sort((left, right) => Date.parse(left.date!) - Date.parse(right.date!))
    .slice(0, 6);
  const capacity = [
    { key: "sac", label: "SAC", ...durationAverage(observed, "created_at", "sac_completed_at") },
    { key: "art", label: "Arte Final", ...durationAverage(observed, "sac_completed_at", "art_completed_at") },
    { key: "production", label: "Produção", ...durationAverage(observed, "production_started_at", "expedition_at") },
    { key: "finance", label: "Faturamento", ...durationAverage(observed, "finance_started_at", "finance_completed_at") },
  ];
  const completeCycle = durationAverage(observed, "created_at", "finance_completed_at");
  const completedLast30Days = observed.filter((item) => {
    const completed = Date.parse(item.finance_completed_at || "");
    return Number.isFinite(completed) && completed >= Date.now() - 30 * 86_400_000;
  }).length;

  return (
    <div className="productive-grid">
      <section className="productive-hero panel">
        <div className="section-heading">
          <div><p className="eyebrow">{settings.subtitle}</p><h2>{settings.title}</h2><p>{settings.description}</p></div>
          <span className="report-date"><CalendarClock size={16} /> Base iniciada em 01 set. 2026</span>
        </div>
        <div className="productive-kpis">
          <div><span>Em andamento</span><strong>{inProgress.length}</strong><small>produção, expedição ou instalação</small></div>
          <div><span>Produção física</span><strong>{active.filter((item) => ["PRODUCAO","SEPARACAO","CONFERENCIA"].includes(item.stage)).length}</strong><small>em giro produtivo</small></div>
          <div><span>Controles finais</span><strong>{active.filter((item) => item.stage === "POS_FINALIZACAO").length}</strong><small>Status Comercial: Conferir OS</small></div>
          <div><span>A faturar</span><strong>{active.filter((item) => ["PRONTO_FINANCEIRO","FATURAMENTO"].includes(item.stage)).length}</strong><small>Status Comercial: Faturar OS</small></div>
          <div><span>Concluídas 100%</span><strong>{processes.filter((item) => item.stage === "CONCLUIDO").length}</strong><small>Status Comercial: OS Faturada</small></div>
        </div>
      </section>

      <section className="panel capacity-panel">
        <div className="section-heading compact"><div><p className="eyebrow">CAPACIDADE MÉDIA PRODUTIVA</p><h2>Tempo observado por etapa</h2><p>Médias calculadas somente com ciclos concluídos desde o início do sistema.</p></div><Gauge size={21} /></div>
        <div className="capacity-summary">
          <div className="capacity-cycle"><span>Ciclo completo</span><strong>{formatDuration(completeCycle.averageHours)}</strong><small>{completeCycle.samples ? `${completeCycle.samples} ${completeCycle.samples === 1 ? "processo concluído" : "processos concluídos"}` : "Aguardando o primeiro ciclo completo"}</small></div>
          <div className="capacity-throughput"><span>Concluídos em 30 dias</span><strong>{completedLast30Days}</strong><small>desde a implantação do fluxo</small></div>
          <div className="capacity-stages">
            {capacity.map((metric) => <div key={metric.key}><span>{metric.label}</span><strong>{formatDuration(metric.averageHours)}</strong><small>{metric.samples ? `${metric.samples} ${metric.samples === 1 ? "amostra" : "amostras"}` : "coleta em andamento"}</small></div>)}
          </div>
        </div>
      </section>

      <section className="panel workload-panel">
        <div className="section-heading compact"><div><p className="eyebrow">CAPACIDADE ATUAL</p><h2>Carga por etapa</h2></div><Gauge size={21} /></div>
        <div className="bar-list">
          {STAGE_GROUPS.map((group) => {
            const count = active.filter((item) => group.stages.includes(item.stage)).length;
            const width = active.length ? Math.max((count / active.length) * 100, count ? 8 : 0) : 0;
            return <div key={group.key} className="bar-row"><div><span>{group.label}</span><strong>{count}</strong></div><div className="bar-track"><i style={{ width: `${width}%` }} /></div><small>{group.owner}</small></div>;
          })}
        </div>
      </section>

      <section className="panel brand-panel">
        <div className="section-heading compact"><div><p className="eyebrow">MARCAS</p><h2>Distribuição da carteira</h2></div><Layers3 size={21} /></div>
        <div className="brand-chart">
          {brandCounts.map(([brand, count]) => (
            <div key={brand} className="brand-chart-row">
              <span className={`brand-dot brand-${brand.toLowerCase()}`} />
              <strong>{brand}</strong>
              <div><i style={{ width: `${(count / maxBrand) * 100}%` }} /></div>
              <b>{count}</b>
            </div>
          ))}
        </div>
      </section>

      <section className="panel forecast-panel">
        <div className="section-heading compact"><div><p className="eyebrow">PRÓXIMAS ENTREGAS</p><h2>Agenda de compromisso</h2></div><CalendarClock size={21} /></div>
        <div className="forecast-list">
          {upcoming.map(({ item, date }) => (
            <div key={item.id}><time>{formatDate(date, true)}</time><span className={`brand-pill brand-${item.brand.toLowerCase()}`}>{item.brand}</span><div><strong>{item.os_number ? `O.S. ${item.os_number}` : "O.S. pendente"} · {item.company}</strong><small>BD {item.bd} · {STAGE_LABELS[item.stage]} · {item.next_action}</small></div></div>
          ))}
        </div>
      </section>
    </div>
  );
}

function AuditView({
  records,
  processes,
  sync,
  onRefresh,
}: {
  records: AuditRecord[];
  processes: FleetProcess[];
  sync: AppPayload["auditSync"];
  onRefresh: () => Promise<void>;
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Todos os status");
  const [selected, setSelected] = useState<AuditRecord | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const linkedByOs = useMemo(() => {
    const map = new Map<string, FleetProcess[]>();
    processes.forEach((process) => {
      if (!process.os_number) return;
      map.set(process.os_number, [...(map.get(process.os_number) || []), process]);
    });
    return map;
  }, [processes]);
  const filtered = useMemo(() => records.filter((record) => {
    const situation = normalizedStatus(record.situation);
    if (status === "Liberadas" && situation !== "liberada") return false;
    if (status === "Pendentes" && situation !== "pendente") return false;
    if (status === "Não faturar" && situation !== "nao faturar") return false;
    if (status === "Pagamento pendente" && normalizedStatus(record.supplier_payment_status) === "pago") return false;
    const term = normalizedStatus(search);
    return !term || [record.os_number, record.company, record.cnpj, record.team, record.supplier_name]
      .some((value) => normalizedStatus(value).includes(term));
  }), [records, search, status]);
  const metrics = {
    total: records.length,
    pending: records.filter((record) => normalizedStatus(record.situation) === "pendente").length,
    blocked: records.filter((record) => normalizedStatus(record.situation) === "nao faturar").length,
    ready: records.filter((record) => normalizedStatus(record.situation) === "liberada" && normalizedStatus(record.supplier_payment_status) === "pago").length,
    paid: records.filter((record) => normalizedStatus(record.supplier_payment_status) === "pago").length,
  };

  async function refresh() {
    setRefreshing(true);
    try { await onRefresh(); }
    finally { setRefreshing(false); }
  }

  return (
    <div className="audit-workspace">
      <section className="workspace-title audit-title">
        <div>
          <p className="eyebrow">AUDITORIA, NOTAS E PAGAMENTO</p>
          <h1>Auditoria O.S.<span>.</span></h1>
          <p>Histórico oficial ligado à programação do Fluxo Frotas pelo número da O.S.</p>
        </div>
        <div className="title-actions">
          <span className={cn(["source-chip", sync.status === "error" && "is-error"])} title={sync.error || "Base sincronizada"}>
            <i /> {sync.status === "error" ? "Sincronização pendente" : `${sync.row_count} auditorias importadas`}
          </span>
          <button className="secondary-button" onClick={() => void refresh()} disabled={refreshing}>
            {refreshing ? <Loader2 className="spin" size={16} /> : <RefreshCw size={16} />} Atualizar auditoria
          </button>
        </div>
      </section>

      <section className="audit-kpis" aria-label="Resumo da Auditoria O.S.">
        <div><span>Histórico completo</span><strong>{metrics.total}</strong><small>registros importados</small></div>
        <div><span>Em auditoria</span><strong>{metrics.pending}</strong><small>aguardando conclusão</small></div>
        <div className={metrics.blocked ? "has-alert" : ""}><span>Não faturar</span><strong>{metrics.blocked}</strong><small>exigem tratativa</small></div>
        <div><span>Prontas</span><strong>{metrics.ready}</strong><small>liberadas e pagas</small></div>
        <div><span>Pagamentos concluídos</span><strong>{metrics.paid}</strong><small>equipes pagas</small></div>
      </section>

      <section className="panel audit-panel">
        <div className="operations-toolbar audit-toolbar">
          <div><p className="eyebrow">FLUXO CONECTADO</p><h2>{filtered.length} auditorias no recorte</h2></div>
          <div className="filters">
            <label className="search-field"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar O.S., cliente, CNPJ ou equipe..." /></label>
            <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filtrar auditorias">
              <option>Todos os status</option><option>Liberadas</option><option>Pendentes</option><option>Não faturar</option><option>Pagamento pendente</option>
            </select>
          </div>
        </div>
        <div className="table-wrap audit-table-wrap">
          <table className="audit-table">
            <thead><tr><th>O.S. / cliente</th><th>Programação</th><th>Auditoria</th><th>Retorno financeiro</th><th>Valores</th><th>Atualização</th><th aria-label="Abrir" /></tr></thead>
            <tbody>
              {filtered.map((record) => {
                const linked = linkedByOs.get(record.os_number) || [];
                const process = linked[0];
                const forecast = linked.map(processForecast).filter((value): value is string => Boolean(value)).sort()[0] || null;
                return (
                  <tr key={record.id} onClick={() => setSelected(record)}>
                    <td><strong>O.S. {record.os_number}</strong><small>{record.company || "Cliente não informado"}</small></td>
                    <td>{process ? <><span className={`stage-pill stage-${process.stage.toLowerCase()}`}>{STAGE_LABELS[process.stage]}</span><small>{linked.length} {linked.length === 1 ? "veículo" : "veículos"}{forecast ? ` · ${formatDate(forecast, true)}` : " · sem previsão"}</small></> : <><span className="audit-badge is-neutral">Histórico</span><small>Sem linha ativa na PCP</small></>}</td>
                    <td><span className={cn(["audit-badge", auditTone(record.situation)])}>{record.situation || "PENDENTE"}</span><small>{record.delivery_status ? `Envio: ${record.delivery_status}` : "Ainda não enviada"}</small></td>
                    <td><strong>{record.supplier_payment_status || "Não informado"}</strong><small>{[record.material_status, record.service_status].filter(Boolean).join(" · ") || "Notas sem status"}</small></td>
                    <td><strong>{formatMoney(record.value_final)}</strong><small className={record.difference ? "value-difference" : ""}>{record.difference ? `${record.difference > 0 ? "+" : ""}${formatMoney(record.difference)}` : "Sem diferença"}</small></td>
                    <td><span>{formatDate(record.source_updated_at || record.audit_date, true)}</span><small>{record.created_by || record.team || "Auditoria O.S."}</small></td>
                    <td><button className="icon-button" aria-label={`Abrir auditoria da O.S. ${record.os_number}`}><ChevronRight size={17} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filtered.length && <div className="empty-state"><ClipboardCheck size={28} /><strong>Nenhuma auditoria neste recorte.</strong><span>Ajuste a busca ou o filtro de status.</span></div>}
        </div>
      </section>

      {selected && (() => {
        const linked = linkedByOs.get(selected.os_number) || [];
        const process = linked[0];
        return (
          <div className="drawer-layer" role="presentation">
            <button className="drawer-backdrop" onClick={() => setSelected(null)} aria-label="Fechar auditoria" />
            <aside className="process-drawer audit-drawer" role="dialog" aria-modal="true" aria-labelledby="audit-drawer-title">
              <header className="drawer-header"><div><p className="eyebrow">REGISTRO {selected.id}</p><h2 id="audit-drawer-title">O.S. {selected.os_number}<span> · Auditoria</span></h2></div><button className="icon-button" onClick={() => setSelected(null)} aria-label="Fechar"><X size={20} /></button></header>
              <div className="drawer-scroll">
                <section className="process-identity audit-identity">
                  <div className="identity-topline"><span className={cn(["audit-badge", auditTone(selected.situation)])}>{selected.situation}</span>{process && <span className={`stage-pill stage-${process.stage.toLowerCase()}`}>{STAGE_LABELS[process.stage]}</span>}</div>
                  <h3>{selected.company}</h3><p>{selected.team || "Equipe não informada"} · {selected.vehicle_count || linked.length} {selected.vehicle_count === 1 ? "veículo" : "veículos"}</p>
                  <div className="audit-flow-summary">
                    <div><small>PROGRAMAÇÃO</small><strong>{process ? STAGE_LABELS[process.stage] : "Histórico encerrado"}</strong><span>{process?.next_action || "Sem linha ativa na PCP"}</span></div>
                    <ArrowRight size={18} />
                    <div><small>AUDITORIA</small><strong>{selected.situation}</strong><span>{selected.delivery_status || "Sem envio"}</span></div>
                    <ArrowRight size={18} />
                    <div><small>FINANCEIRO</small><strong>{selected.supplier_payment_status || "Não informado"}</strong><span>{[selected.material_status, selected.service_status].filter(Boolean).join(" · ") || "Aguardando retorno"}</span></div>
                  </div>
                </section>

                <section className="drawer-section"><div className="drawer-section-title"><CircleDollarSign size={17} /><h3>Notas, planilha e pagamento</h3></div><div className="detail-grid audit-finance-grid">
                  <div><span>Nota remessa</span><strong>{selected.dispatch_note || "—"}</strong></div><div><span>Nota material</span><strong>{selected.material_note || "—"}</strong></div><div><span>Nota serviço</span><strong>{selected.service_note || "—"}</strong></div><div><span>Status material</span><strong>{selected.material_status || "—"}</strong></div><div><span>Status serviço</span><strong>{selected.service_status || "—"}</strong></div><div><span>Pagamento da equipe</span><strong>{selected.supplier_payment_status || "—"}</strong></div>
                </div></section>

                <section className="drawer-section"><div className="drawer-section-title"><FileText size={17} /><h3>Conclusão da auditoria</h3></div><div className="audit-copy-block"><span>Orientação ao financeiro</span><p>{selected.finance_instruction || "Nenhuma orientação registrada."}</p></div><div className="audit-copy-block"><span>Resumo</span><p>{selected.summary || "Nenhum resumo registrado."}</p></div><div className="audit-values"><div><span>Antes</span><strong>{formatMoney(selected.value_before)}</strong></div><div><span>Diferença</span><strong>{formatMoney(selected.difference)}</strong></div><div><span>Final</span><strong>{formatMoney(selected.value_final)}</strong></div></div></section>

                <section className="drawer-section"><div className="drawer-section-title"><Activity size={17} /><h3>Alterações por veículo</h3></div><div className="audit-change-list">
                  {selected.changes.map((change) => <div key={change.id}><span className={cn(["audit-badge", change.financial_impact < 0 ? "is-danger" : "is-neutral"])}>{change.change_type || "AJUSTE"}</span><div><strong>{change.plate || "Sem placa"} · {formatMoney(change.financial_impact)}</strong><p>{change.reason || "Sem justificativa registrada."}</p></div></div>)}
                  {!selected.changes.length && <p className="muted">Nenhuma alteração vinculada a esta auditoria.</p>}
                </div></section>

                {(selected.pdf_url || selected.evidence_urls.length > 0) && <section className="drawer-section"><div className="drawer-section-title"><ExternalLink size={17} /><h3>Documentos</h3></div><div className="audit-links">{selected.pdf_url && <a href={selected.pdf_url} target="_blank" rel="noreferrer noopener"><FileText size={16} /> Relatório da auditoria <ExternalLink size={14} /></a>}{selected.evidence_urls.map((url, index) => <a key={url} href={url} target="_blank" rel="noreferrer noopener"><ImageIcon size={16} /> Evidência {index + 1} <ExternalLink size={14} /></a>)}</div></section>}
              </div>
            </aside>
          </div>
        );
      })()}
    </div>
  );
}

function IntegrationsView({ integrations }: { integrations: AppPayload["integrations"] }) {
  const cards = [
    { key: "sheet", title: "PCP FROTA C1", description: "Base consolidada atual. Nesta homologação, o sistema não altera a planilha.", url: integrations.sheet, icon: FileText },
    { key: "audit", title: "Auditoria de O.S.", description: "Validação obrigatória após o status U Finalizado.", url: integrations.audit, icon: ClipboardCheck },
    { key: "payment", title: "Pagamentos operacionais", description: "Controle paralelo à auditoria, antes da liberação financeira.", url: integrations.payment, icon: CircleDollarSign },
    { key: "fleetDashboard", title: "Painel de Frotas", description: "Painel consolidado existente, mantido sem alterações nesta fase.", url: integrations.fleetDashboard, icon: LayoutDashboard },
  ];

  return (
    <section className="panel integrations-panel">
      <div className="section-heading">
        <div><p className="eyebrow">CONEXÕES OFICIAIS</p><h2>Painéis e fonte de dados</h2><p>Atalhos seguros para os sistemas já consolidados.</p></div>
      </div>
      <div className="integration-grid">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <article key={card.key} className="integration-card">
              <span className="integration-icon"><Icon size={22} /></span>
              <div><h3>{card.title}</h3><p>{card.description}</p></div>
              <div className="integration-footer">
                <span className="health-status is-ok"><i />Ativo</span>
                <a href={card.url} target="_blank" rel="noreferrer noopener">Abrir <ExternalLink size={14} /></a>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function AdminView({
  payload,
  onSaveSettings,
  onSetAuditAccess,
}: {
  payload: AppPayload;
  onSaveSettings: (settings: ProductiveSettings) => Promise<void>;
  onSetAuditAccess: (userId: number, canView: boolean) => Promise<void>;
}) {
  const [content, setContent] = useState(payload.settings);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);
  const [accessBusy, setAccessBusy] = useState<number | null>(null);
  const matrix = [
    ["Administração", "Configura usuários, parâmetros globais e conteúdo institucional, com visão integral da operação."],
    ["Frotas", "Gerencia cadastro, SAC, sequenciamento, expedição, rota, instalação e controles pós-execução."],
    ["Comercial", "Consulta o processo completo e mantém exclusivamente o registro e a liberação da O.S."],
    ["Arte Final", "Planeja a entrega criativa, registra a conclusão e disponibiliza os arquivos aprovados para produção."],
    ["Produção", "Planeja prazos e registra execução, separação, conferência e liberação do material para expedição."],
    ["Financeiro", "Recebe processos validados, registra o faturamento e conclui a etapa financeira."],
    ["Diretoria", "Acompanha indicadores, prazos e capacidade produtiva em modo exclusivamente consultivo."],
  ];

  async function saveContent(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setFailed(false);
    try {
      await onSaveSettings(content);
      setMessage("Conteúdo salvo e publicado para todos os acessos autorizados.");
    } catch (cause) {
      setFailed(true);
      setMessage(cause instanceof Error ? cause.message : "Não foi possível salvar o conteúdo.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleAuditAccess(userId: number, canView: boolean) {
    setAccessBusy(userId);
    try { await onSetAuditAccess(userId, canView); }
    finally { setAccessBusy(null); }
  }

  return (
    <div className="admin-grid">
      <section className="panel">
        <div className="section-heading compact"><div><p className="eyebrow">ACESSOS</p><h2>Usuários de homologação</h2></div><Users size={21} /></div>
        <div className="user-list">
          {payload.users?.map((user) => (
            <div key={user.id} className="user-access-row">
              <span className="avatar">{user.name.charAt(0)}</span><div className="user-access-identity"><strong>{user.name}</strong><small>{user.email}</small></div><span className="role-chip">{ROLE_LABELS[user.role]}</span>
              <label className="access-switch" title="Permitir acesso à aba Auditoria O.S.">
                <input type="checkbox" checked={Boolean(user.audit_os_access)} disabled={user.role === "ADMIN" || accessBusy === user.id} onChange={(event) => void toggleAuditAccess(user.id, event.target.checked)} />
                <span aria-hidden="true" />
                <small>{accessBusy === user.id ? "Salvando..." : "Auditoria O.S."}</small>
              </label>
              <i className={user.active ? "is-active" : ""} />
            </div>
          ))}
        </div>
        <p className="access-note"><ShieldCheck size={15} /> A permissão é validada no servidor. Usuários sem acesso não recebem os dados da auditoria.</p>
      </section>
      <section className="panel">
        <div className="section-heading compact"><div><p className="eyebrow">GOVERNANÇA DE ACESSO</p><h2>Perfis e responsabilidades</h2><p>Escopos definidos pelo princípio do menor privilégio.</p></div><ShieldCheck size={21} /></div>
        <div className="permission-matrix">
          {matrix.map(([role, rights]) => <div key={role}><strong>{role}</strong><span>{rights}</span></div>)}
        </div>
      </section>
      <form className="panel admin-wide content-settings" onSubmit={saveContent}>
        <div className="section-heading compact"><div><p className="eyebrow">CONTEÚDO GLOBAL</p><h2>Apresentação da Visão Produtiva</h2><p>Alterações publicadas para Frotas, Comercial, Diretoria e painel consolidado.</p></div><Settings2 size={21} /></div>
        <div className="content-settings-grid">
          <label>Título<input maxLength={90} value={content.title} onChange={(event) => setContent({ ...content, title: event.target.value })} required /></label>
          <label>Subtítulo<input maxLength={120} value={content.subtitle} onChange={(event) => setContent({ ...content, subtitle: event.target.value })} required /></label>
          <label className="span-two">Descrição<textarea maxLength={360} rows={3} value={content.description} onChange={(event) => setContent({ ...content, description: event.target.value })} required /></label>
        </div>
        <div className="content-settings-actions">
          {message && <p className={failed ? "form-error" : "save-feedback"}>{failed ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />}{message}</p>}
          <button className="primary-button" disabled={busy}>{busy ? <Loader2 className="spin" size={17} /> : <Check size={17} />} Salvar para todos</button>
        </div>
      </form>
    </div>
  );
}

function NewProcessModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => Promise<void> }) {
  const [form, setForm] = useState({ bd: "", company: "", cnpj: "", brand: "Copa", state: "SP", city: "", priority: "NORMAL" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setMessage("");
    try { await api.createProcess(form); await onCreated(); onClose(); }
    catch (cause) { setMessage(cause instanceof Error ? cause.message : "Falha ao criar."); }
    finally { setBusy(false); }
  }
  return (
    <div className="modal-layer">
      <button className="modal-backdrop" onClick={onClose} aria-label="Fechar" />
      <form className="modal-card" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="new-process-title">
        <div className="modal-header"><div><p className="eyebrow">NOVO PROCESSO</p><h2 id="new-process-title">Entrada por Frotas</h2></div><button type="button" className="icon-button" onClick={onClose} aria-label="Fechar novo processo"><X size={19} /></button></div>
        <div className="form-grid">
          <label>BD<input value={form.bd} onChange={(event) => setForm({ ...form, bd: event.target.value })} required /></label>
          <label>Marca<select value={form.brand} onChange={(event) => setForm({ ...form, brand: event.target.value })}>{SHEET_BRANDS.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="span-two">Razão social<input value={form.company} onChange={(event) => setForm({ ...form, company: event.target.value })} required /></label>
          <label>CNPJ<input value={form.cnpj} onChange={(event) => setForm({ ...form, cnpj: event.target.value })} /></label>
          <label>Cidade<input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} /></label>
          <label>UF<input maxLength={2} value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value.toUpperCase() })} /></label>
          <label>Prioridade<select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}><option>NORMAL</option><option>ALTA</option><option>PILOTO</option></select></label>
        </div>
        {message && <p className="form-error"><AlertCircle size={15} /> {message}</p>}
        <div className="modal-actions"><button type="button" className="ghost-button" onClick={onClose}>Cancelar</button><button className="primary-button" disabled={busy}>{busy ? <Loader2 className="spin" size={17} /> : <Plus size={17} />} Criar processo</button></div>
      </form>
    </div>
  );
}

function Workspace({ initialPayload, onLogout }: { initialPayload: AppPayload; onLogout: () => void }) {
  const [payload, setPayload] = useState(initialPayload);
  const [page, setPage] = useState<Page>(
    initialPayload.user.role === "DIRETORIA"
      ? "productive"
      : initialPayload.user.role === "ADMIN"
        ? "admin"
        : "queue",
  );
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("Todas as marcas");
  const [fleetStatus, setFleetStatus] = useState("Todos os status");
  const [osStatus, setOsStatus] = useState("Todos Status Comercial");
  const [tablePage, setTablePage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState("");

  const reload = useCallback(async (forceSync = false) => {
    setRefreshing(true);
    try {
      if (forceSync && ["ADMIN", "FROTAS"].includes(payload.user.role)) await api.syncSheet();
      setPayload(await api.app());
    }
    finally { setRefreshing(false); }
  }, [payload.user.role]);

  const queue = useMemo(() => payload.processes.filter((item) => ROLE_STAGE_QUEUE[payload.user.role].includes(item.stage) && !TERMINAL_STAGES.includes(item.stage) && item.stage !== "PAUSADO"), [payload]);
  const schedule = useMemo(() => payload.processes.filter((item) => !TERMINAL_STAGES.includes(item.stage) && item.stage !== "PAUSADO" && Boolean(processForecast(item))), [payload.processes]);
  const history = useMemo(() => payload.processes.filter((item) => TERMINAL_STAGES.includes(item.stage) || item.stage === "PAUSADO"), [payload.processes]);
  const filtered = useMemo(() => {
    const base = page === "queue" ? queue : page === "schedule" ? schedule : history;
    const term = search.trim().toLocaleLowerCase("pt-BR");
    const result = base.filter((item) => {
      if (brand !== "Todas as marcas" && item.brand !== brand) return false;
      if (fleetStatus !== "Todos os status" && item.fleet_status !== fleetStatus) return false;
      if (osStatus !== "Todos Status Comercial" && item.os_status !== osStatus) return false;
      if (!term) return true;
      return [item.bd, item.os_number, item.company, item.city, item.code, item.vehicle_type, item.vehicle_model, item.vehicle_plate].filter(Boolean).some((value) => String(value).toLocaleLowerCase("pt-BR").includes(term));
    });
    return [...result].sort((left, right) => {
      if (page === "history") return Date.parse(right.updated_at) - Date.parse(left.updated_at);
      if (page === "schedule") return Date.parse(processForecast(left) || "2999-12-31") - Date.parse(processForecast(right) || "2999-12-31");
      const order: Record<QueueBucket, number> = { urgent: 0, now: 1, scheduled: 2 };
      const bucketDifference = order[processQueueBucket(left)] - order[processQueueBucket(right)];
      if (bucketDifference) return bucketDifference;
      return Date.parse(processForecast(left) || "2999-12-31") - Date.parse(processForecast(right) || "2999-12-31");
    });
  }, [brand, fleetStatus, history, osStatus, page, queue, schedule, search]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / TABLE_PAGE_SIZE));
  const pagedProcesses = useMemo(
    () => filtered.slice((tablePage - 1) * TABLE_PAGE_SIZE, tablePage * TABLE_PAGE_SIZE),
    [filtered, tablePage],
  );

  useEffect(() => {
    setTablePage(1);
  }, [brand, fleetStatus, osStatus, page, search]);

  useEffect(() => {
    if (tablePage > totalPages) setTablePage(totalPages);
  }, [tablePage, totalPages]);

  const selected = payload.processes.find((item) => item.id === selectedId) || null;
  const queueGroups = useMemo(() => buildProcessGroups(queue), [queue]);
  const urgentGroups = queueGroups.filter((group) => group.bucket === "urgent").length;
  const nowGroups = queueGroups.filter((group) => group.bucket === "now").length;
  const scheduledGroups = queueGroups.filter((group) => group.bucket === "scheduled").length;
  const canViewProductive = PRODUCTIVE_ROLES.includes(payload.user.role);

  const navItems: Array<{ key: Page; label: string; icon: typeof Activity; hidden?: boolean; count?: number }> = [
    { key: "queue", label: "Minha fila", icon: Activity, count: queueGroups.length },
    { key: "schedule", label: "Programação", icon: CalendarClock },
    { key: "history", label: "Histórico", icon: FileText },
    { key: "audit", label: "Auditoria O.S.", icon: ClipboardCheck, hidden: !payload.features.auditOs },
    { key: "productive", label: "Visão produtiva", icon: BarChart3, hidden: !canViewProductive },
    { key: "integrations", label: "Integrações", icon: Layers3, hidden: payload.user.role !== "ADMIN" },
    { key: "admin", label: "Administração", icon: Settings2, hidden: payload.user.role !== "ADMIN" },
  ];

  async function mutate(action: string, actionPayload: Record<string, unknown>) {
    if (!selectedId) return;
    const result = await api.action(selectedId, action, actionPayload);
    setPayload((current) => ({ ...current, processes: current.processes.map((item) => item.id === result.process.id ? result.process : item) }));
    setToast("Processo atualizado com sucesso.");
    window.setTimeout(() => setToast(""), 2800);
    void reload();
  }

  async function saveProductiveSettings(settings: ProductiveSettings) {
    const result = await api.updateProductiveSettings(settings);
    setPayload((current) => ({ ...current, settings: result.settings }));
  }

  async function refreshAudit() {
    if (["ADMIN", "FROTAS"].includes(payload.user.role)) await api.syncAudit();
    setPayload(await api.app());
  }

  async function setAuditAccess(userId: number, canView: boolean) {
    await api.updateAuditAccess(userId, canView);
    setPayload((current) => ({
      ...current,
      users: current.users?.map((user) => user.id === userId ? { ...user, audit_os_access: canView ? 1 : 0 } : user),
    }));
  }

  async function logout() {
    await api.logout().catch(() => undefined);
    onLogout();
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <Logo />
        <nav className={mobileMenu ? "is-open" : ""} aria-label="Navegação principal">
          {navItems.filter((item) => !item.hidden).map((item) => {
            const Icon = item.icon;
            return <button key={item.key} className={page === item.key ? "is-active" : ""} onClick={() => { setPage(item.key); setMobileMenu(false); }}><Icon size={16} />{item.label}{item.count !== undefined && <span>{item.count}</span>}</button>;
          })}
        </nav>
        <div className="header-tools">
          <span
            className={cn(["environment-chip", payload.sync.status === "error" && "is-error"])}
            title={payload.sync.error || `Última leitura: ${formatDate(payload.sync.completed_at, true)}`}
          ><i /> {payload.sync.status === "error" ? "SINCRONIZAÇÃO PENDENTE" : "PCP ATIVA"}</span>
          <div className="user-menu"><span className="avatar">{payload.user.name.charAt(0)}</span><div><strong>{shortName(payload.user.name)}</strong><small>{ROLE_LABELS[payload.user.role]}</small></div></div>
          <button className="icon-button logout-button" onClick={() => void logout()} aria-label="Sair"><LogOut size={17} /></button>
          <button className="icon-button mobile-menu-button" onClick={() => setMobileMenu(!mobileMenu)} aria-label="Abrir menu"><Menu size={20} /></button>
        </div>
      </header>

      <main className="workspace">
        {(["queue", "schedule", "history"] as Page[]).includes(page) && (
          <>
            <section className="workspace-title">
              <div>
                <p className="eyebrow">{page === "queue" ? ROLE_LABELS[payload.user.role].toUpperCase() : page === "schedule" ? "PRAZOS DA OPERAÇÃO" : "CONSULTA"}</p>
                <h1>{page === "queue" ? <>Minha fila<span>.</span></> : page === "schedule" ? <>Programação<span>.</span></> : <>Histórico<span>.</span></>}</h1>
                <p>
                  {page === "queue"
                    ? `${queueGroups.length} ${queueGroups.length === 1 ? "O.S. precisa" : "O.S. precisam"} de atenção do seu setor.`
                    : page === "schedule"
                      ? "Acompanhe os prazos em ordem cronológica; previsões vencidas aparecem em vermelho."
                      : "Consulte processos concluídos, pausados ou cancelados sem misturá-los à operação do dia."}
                </p>
              </div>
              <div className="title-actions">
                <button className="secondary-button" onClick={() => void reload(true)} disabled={refreshing}>{refreshing ? <Loader2 className="spin" size={16} /> : <RefreshCw size={16} />} Atualizar PCP</button>
                {(payload.user.role === "ADMIN" || payload.user.role === "FROTAS") && <button className="primary-button" onClick={() => setCreating(true)}><Plus size={17} /> Novo processo</button>}
              </div>
            </section>

            {page === "queue" && (
              <>
                <section className="queue-summary" aria-label="Resumo da fila">
                  <div className="is-urgent"><span><AlertCircle size={17} /> Atrasados ou prioritários</span><strong>{urgentGroups}</strong><small>O.S. para começar primeiro</small></div>
                  <div><span><Zap size={17} /> Para fazer agora</span><strong>{nowGroups}</strong><small>O.S. com ação disponível</small></div>
                  <div><span><CalendarClock size={17} /> Programados</span><strong>{scheduledGroups}</strong><small>O.S. com prazo futuro</small></div>
                </section>
                <details className="queue-help">
                  <summary>Como usar esta tela</summary>
                  <div><span><b>1</b> Comece pelo primeiro bloco.</span><span><b>2</b> Abra a O.S. e escolha o veículo.</span><span><b>3</b> Execute a ação indicada no painel lateral.</span></div>
                </details>
              </>
            )}

            <section className="operations-panel panel">
              <div className="operations-toolbar">
                <div>
                  <p className="eyebrow">{page === "queue" ? "AÇÕES DO SEU SETOR" : page === "schedule" ? "AGENDA OPERACIONAL" : "ARQUIVO OPERACIONAL"}</p>
                  <h2>{page === "queue" ? `${buildProcessGroups(filtered).length} O.S. · ${filtered.length} ${filtered.length === 1 ? "veículo" : "veículos"}` : `${filtered.length} ${filtered.length === 1 ? "processo" : "processos"}`}</h2>
                </div>
                <div className="filters">
                  <label className="search-field"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar O.S., cliente, veículo ou placa..." /></label>
                  <select value={brand} onChange={(event) => setBrand(event.target.value)} aria-label="Filtrar por marca"><option>Todas as marcas</option>{SHEET_BRANDS.map((item) => <option key={item}>{item}</option>)}</select>
                  <select value={fleetStatus} onChange={(event) => setFleetStatus(event.target.value)} aria-label="Filtrar por Status da coluna U"><option>Todos os status</option>{STATUS_U_OPTIONS.map((item) => <option key={item}>{item}</option>)}</select>
                  <select value={osStatus} onChange={(event) => setOsStatus(event.target.value)} aria-label="Filtrar por Status Comercial"><option>Todos Status Comercial</option>{STATUS_COMMERCIAL_OPTIONS.map((item) => <option key={item}>{item}</option>)}</select>
                </div>
              </div>
              {page === "queue"
                ? <ActionQueue processes={pagedProcesses} onSelect={(process) => setSelectedId(process.id)} />
                : <ProcessTable processes={pagedProcesses} selectedId={selectedId} onSelect={(process) => setSelectedId(process.id)} />}
              {filtered.length > 0 && (
                <footer className="table-pagination" aria-label="Paginação da lista de processos">
                  <span>{(tablePage - 1) * TABLE_PAGE_SIZE + 1}–{Math.min(tablePage * TABLE_PAGE_SIZE, filtered.length)} de {filtered.length}</span>
                  <div>
                    <button type="button" className="icon-button" onClick={() => setTablePage((current) => Math.max(1, current - 1))} disabled={tablePage === 1} aria-label="Página anterior"><ChevronLeft size={16} /></button>
                    <strong>Página {tablePage} de {totalPages}</strong>
                    <button type="button" className="icon-button" onClick={() => setTablePage((current) => Math.min(totalPages, current + 1))} disabled={tablePage === totalPages} aria-label="Próxima página"><ChevronRight size={16} /></button>
                  </div>
                </footer>
              )}
            </section>
          </>
        )}

        {page === "productive" && canViewProductive && <ProductiveView processes={payload.processes} settings={payload.settings} />}
        {page === "audit" && payload.features.auditOs && <AuditWorkspace user={payload.user} sync={payload.auditSync} onRefresh={refreshAudit} />}
        {page === "integrations" && <IntegrationsView integrations={payload.integrations} />}
        {page === "admin" && payload.user.role === "ADMIN" && <AdminView payload={payload} onSaveSettings={saveProductiveSettings} onSetAuditAccess={setAuditAccess} />}
      </main>

      {selected && <ProcessDrawer process={selected} userRole={payload.user.role} events={payload.events} onClose={() => setSelectedId(null)} onMutate={mutate} />}
      {creating && <NewProcessModal onClose={() => setCreating(false)} onCreated={reload} />}
      {toast && <div className="toast" role="status"><CheckCircle2 size={18} />{toast}</div>}
    </div>
  );
}

export default function App() {
  const [payload, setPayload] = useState<AppPayload | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.app().then((data) => { if (!cancelled) setPayload(data); }).catch(() => undefined).finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, []);

  if (checking) {
    return <div className="boot-screen"><Logo /><Loader2 className="spin" size={22} /><span>Preparando o trilho produtivo...</span></div>;
  }
  if (!payload) return <Login onLogin={setPayload} />;
  return <Workspace initialPayload={payload} onLogout={() => setPayload(null)} />;
}
